<template>
  <v-container
    id="wizard"
    tag="section"
  >
    <base-material-wizard
      v-model="tab"
      :available-steps="availableSteps"
      :items="tabs"
      :loading="loading"
      class="mx-auto"
      color="light-blue accent-3"
      @click:next="next"
      @click:prev="tab--"
      @tab:change="save"
    >
      <v-tab-item class="pb-12">
        <form
          action=""
          data-vv-scope="enroller"
        >
          <div class="text-center display-1 font-weight-light mb-6">
            Specify Enroller Details
          </div>
          <v-row
            class="mx-auto"
            justify="space-around"
          >
            <v-col cols="12">
              <v-text-field
                v-model="item.firstName"
                :error-messages="formErrors.FirstName"
                data-vv-name="firstName"
                label="First Name"
                @change="handleEnrollerChange"
              />
            </v-col>
          </v-row>
          <v-row
            class="mx-auto"
            justify="space-around"
          >
            <v-col cols="12">
              <v-text-field
                v-model="item.lastName"
                :error-messages="formErrors.LastName"
                data-vv-name="lastName"
                label="Last Name"
                @change="handleEnrollerChange"
              />
            </v-col>
          </v-row>
          <v-row
            class="mx-auto"
            justify="space-around"
          >
            <v-col cols="12">
              <v-text-field
                v-model="item.email"
                :error-messages="formErrors.Email"
                data-vv-name="email"
                label="Email"
                @change="handleEnrollerChange"
              />
            </v-col>
          </v-row>
        </form>
      </v-tab-item>

      <v-tab-item class="pb-12">
        <form
          action=""
          data-vv-scope="details"
        >
          <v-row
            class="mx-auto"
            justify="space-around"
          >
            <v-col cols="12">
              <base-resource-grid
                color="light-blue accent-3"
                icon="mdi-format-list-text"
                plural-name="Details"
                singular-name="Detail"
                allow-expand
                :headers="headers"
                :resource="enrollerDetailResource"
                :fetch-method="getEnrollerDetailList"
                :prepare-save="prepareSave"
              >
                <template v-slot:editForm="bindings">
                  <v-row>
                    <v-col
                      cols="12"
                    >
                      <base-resource-select
                        v-if="item"
                        label="Broker"
                        item-text="broker"
                        item-value="id"
                        bind-text="broker"
                        bind-value="brokerId"
                        :bind-item="bindings.editedItem"
                        :error-messages="bindings.formErrors.BrokerId"
                        :resource="brokerResource"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col
                      cols="12"
                    >
                      <base-resource-select
                        v-if="item"
                        label="Carrier"
                        item-text="carrier"
                        item-value="id"
                        bind-text="carrier"
                        bind-value="carrierId"
                        :bind-item="bindings.editedItem"
                        :error-messages="bindings.formErrors.CarrierId"
                        :resource="carrierResource"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col
                      cols="12"
                    >
                      <v-text-field
                        v-model="bindings.editedItem.writingNumber"
                        :error-messages="bindings.formErrors.WritingNumber"
                        label="Writing #"
                      />
                    </v-col>
                  </v-row>
                </template>
              </base-resource-grid>
            </v-col>
          </v-row>
        </form>
      </v-tab-item>
      <template v-slot:buttons>
        <v-btn
          color="light-blue accent-3 darken-1"
          @click="finished"
        >
          Close
        </v-btn>
      </template>
    </base-material-wizard>
    <base-material-snackbar
      v-model="displayError"
      type="error"
      v-bind="{
        top: true,
        right: true
      }"
    >
      {{ errorMessage }}
    </base-material-snackbar>
  </v-container>
</template>

<script>
  import TroodyApi from '@/services/troody/TroodyApi'
  import { extractFormErrors, getErrorTitle } from '@/services/ErrorHandlers'

  export default {
    name: 'EnrollerWizard',

    props: {
      editedItem: {
        type: Object,
        default: null,
      },
    },

    data: () => ({
      tab: 0,
      tabs: ['Enroller', 'Details'],
      item: null,

      brokerEnroller: {},

      headers: [
        {
          text: 'Broker',
          value: 'broker',
        },
        {
          text: 'Carrier',
          value: 'carrier',
        },
        {
          text: 'Writing #',
          value: 'writingNumber',
        },
      ],

      loading: false,
      errorMessage: null,
      displayError: false,

      userRefreshKey: 0,

      enrollerDirty: false,
      availableSteps: [0],
      formErrors: {},

      enrollerResource: new TroodyApi('enroller/'),
      enrollerDetailResource: new TroodyApi('enrollerDetail/'),
      brokerResource: new TroodyApi('broker/'),
      carrierResource: new TroodyApi('carrier/'),
    }),

    computed: {
      scope () {
        if (this.tab === 0) return 'enroller'
        return 'details'
      },
    },

    watch: {
      editedItem (val) {
        this.initialize()
      },
    },

    created () {
      this.initialize()
    },

    methods: {
      initialize () {
        this.item = Object.assign({}, this.editedItem)
        this.setAvailableSteps()

        this.enrollerDirty = false
        this.detailsDirty = false
        this.formErrors = {}

        this.tab = 0
      },

      setAvailableSteps () {
        const steps = [0]

        if (this.item.firstName && this.item.lastName) {
          steps.push(1)
        }

        this.availableSteps = steps
      },

      handleEnrollerChange () {
        this.enrollerDirty = true
        this.setAvailableSteps()
      },

      handleDetailChange (detail) {
        this.detailIdSelected = detail.detailId
        this.userRefreshKey += 1 // Force the component to refresh
        this.setAvailableSteps()
      },

      finished () {
        this.save()
          .then(() => {
            this.formErrors = {}
            this.$emit('wizard:finished')
          })
      },

      next () {
        this.loading = true
        this.save()
          .then(() => {
            this.loading = false
            if (this.tab === this.tabs.length - 1) {
              this.finished()
            } else {
              this.tab++
            }
          })
      },

      handleError (error) {
        this.loading = false
        this.formErrors = extractFormErrors(error)
        if (this.formErrors) {
          this.showError(getErrorTitle(error), 'Failed to save!')
        } else {
          this.formErrors = {}
          this.showError(error, 'Failed to save!')
        }
      },

      showError (error, msg) {
        console.log(error)
        this.errorMessage = (error || msg)
        this.displayError = true
      },

      saveEnroller () {
        const promise = new Promise((resolve, reject) => {
          this.enrollerResource.save(this.item)
            .then((data) => {
              this.item = data
              this.enrollerDirty = false
              this.save() // Save anything left for the other steps
              this.$emit('enroller:saved', data)
              resolve(data)
            })
            .catch((error) => {
              this.tab = 0
              reject(error)
            })
        })

        return promise
      },

      save () {
        const promise = new Promise((resolve, reject) => {
          if (this.enrollerDirty) {
            this.saveEnroller()
              .then((data) => {
                resolve(data)
              })
              .catch((error) => {
                this.handleError(error)
                reject(error)
              })
          } else {
            resolve({ enrollerDirty: false })
          }
        })

        return promise
      },

      prepareSave (payload) {
        if (payload) {
          payload.enrollerId = this.item.id
        }
        return payload
      },

      getEnrollerDetailList () {
        return this.enrollerDetailResource.query({ enrollerId: this.item.id })
      },
    },
  }
</script>
